/**
 * `/experiments` — admin Experiment list (#25).
 *
 * Layout:
 *
 *   - Heading row     — page title + "New experiment" button.
 *   - Filter bar      — site dropdown (required to load), status dropdown.
 *                        State is controlled here; changes reset `page` to
 *                        1.
 *   - Table (md+)     — name, status chip, variant count, goal event,
 *                        last updated (relative). Click a row to open the
 *                        detail page.
 *   - Card list (xs)  — same data stacked vertically.
 *
 * Responsive breakpoints:
 *   - 360px / xs    — card list.
 *   - 768px / sm-md — same card list (table activates from md+).
 *   - 1280px / lg+  — table view.
 */

import { useMemo, useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "@tanstack/react-router";
import {
  EXPERIMENT_STATUSES,
  type CreateExperimentInput,
  type ExperimentResponse,
  type ExperimentStatus,
  type ExperimentTargetKind,
  type SiteResponse,
} from "@ottopilot/shared";
import { useCreateExperiment, useExperiments } from "../hooks/useExperiments";
import { usePages } from "../hooks/usePages";
import { useSites } from "../hooks/useSites";
import { ExperimentStatusChip } from "../features/experiments/ExperimentStatusChip";
import { useToast } from "../components/ToastProvider";

function indexSitesById(sites: SiteResponse[] | undefined): Map<string, SiteResponse> {
  const out = new Map<string, SiteResponse>();
  if (!sites) return out;
  for (const s of sites) out.set(s.id, s);
  return out;
}

/**
 * Format an ISO timestamp as a short relative-time string ("2h ago",
 * "yesterday"). Mirrors the leads slice's `formatRelativeTime` utility
 * but inlined so we don't take a dependency on the leads feature folder
 * for a one-line helper.
 */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function ExperimentRow({
  experiment,
  onClick,
}: {
  experiment: ExperimentResponse;
  onClick: () => void;
}): ReactElement {
  return (
    <TableRow
      hover
      onClick={onClick}
      sx={{ cursor: "pointer" }}
      tabIndex={0}
      data-testid="experiments-list-row"
      data-experiment-id={experiment.id}
    >
      <TableCell data-testid="experiments-list-row-name">
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {experiment.name}
        </Typography>
      </TableCell>
      <TableCell>
        <ExperimentStatusChip status={experiment.status} />
      </TableCell>
      <TableCell data-testid="experiments-list-row-variant-count">
        {experiment.variantCount}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {experiment.goalEvent ?? "—"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {relativeTime(experiment.updatedAt)}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

function ExperimentCard({
  experiment,
  onClick,
}: {
  experiment: ExperimentResponse;
  onClick: () => void;
}): ReactElement {
  return (
    <Card variant="outlined" data-testid="experiments-list-card">
      <CardActionArea onClick={onClick} data-experiment-id={experiment.id}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap>
                {experiment.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {experiment.variantCount} variant{experiment.variantCount === 1 ? "" : "s"}
                {experiment.goalEvent ? ` • Goal: ${experiment.goalEvent}` : ""}
              </Typography>
            </Box>
            <ExperimentStatusChip status={experiment.status} />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Updated {relativeTime(experiment.updatedAt)}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function CreateExperimentDialog({
  open,
  siteId,
  onClose,
  onCreated,
}: {
  open: boolean;
  siteId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}): ReactElement {
  const create = useCreateExperiment(siteId);
  const toast = useToast();
  const [name, setName] = useState("");
  // Issue #247: experiment-level target kind. Defaults to PAGE_VERSION
  // (the common path — full-page A/B). Switching to CONTENT_OVERRIDES
  // makes the Page picker optional and re-enables the "All pages"
  // sentinel for site-scoped global-ContentItem overrides.
  const [targetKind, setTargetKind] = useState<ExperimentTargetKind>("PAGE_VERSION");
  // Empty string sentinel = "All pages on this site". For PAGE_VERSION
  // experiments the API requires a page (the mutation body omits
  // `pageId` only when CONTENT_OVERRIDES is selected); for
  // CONTENT_OVERRIDES the empty sentinel means site-scoped overrides.
  const [pageId, setPageId] = useState<string>("");

  // Pull the first 50 pages on this site — same page-size SiteDetailPage
  // uses for its pages tab. Sites with more than 50 pages are rare at MVP
  // scale; if that becomes common we'd switch this to a typeahead.
  const pages = usePages(siteId || undefined, { page: 1, pageSize: 50 });
  const pageList = pages.data?.data ?? [];
  const pagesLoading = pages.isPending;
  const pagesEmpty = !pagesLoading && pageList.length === 0;

  // Issue #247: the Page select is REQUIRED on PAGE_VERSION (the API
  // returns 422 `PAGE_ID_REQUIRED_FOR_PAGE_VERSION_EXPERIMENT`
  // otherwise). On CONTENT_OVERRIDES it stays optional.
  const isPageVersion = targetKind === "PAGE_VERSION";
  const pageRequired = isPageVersion;
  const canSubmit = Boolean(name.trim()) && !create.isPending && (!pageRequired || Boolean(pageId));

  const handleClose = () => {
    if (create.isPending) return;
    setName("");
    setPageId("");
    setTargetKind("PAGE_VERSION");
    onClose();
  };

  const handleSubmit = () => {
    if (!name.trim()) return;
    if (pageRequired && !pageId) return;
    const body: CreateExperimentInput = pageId
      ? { name: name.trim(), targetKind, pageId }
      : { name: name.trim(), targetKind };
    create.mutate(body, {
      onSuccess: (created) => {
        toast.showSuccess("Experiment created.");
        setName("");
        setPageId("");
        setTargetKind("PAGE_VERSION");
        onClose();
        onCreated(created.id);
      },
      onError: (err) => toast.showError(err.message || "Could not create experiment."),
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>New experiment</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
            data-testid="experiment-create-name-input"
          />
          {/* Issue #247: target-kind radio. Determines what kind of
              variants the experiment can carry — full-page swaps vs
              block-level overrides — and whether the Page select is
              required. */}
          <FormControl>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Target type
            </Typography>
            <RadioGroup
              row
              value={targetKind}
              onChange={(e) => {
                const next = e.target.value as ExperimentTargetKind;
                setTargetKind(next);
                // Switching to CONTENT_OVERRIDES → reset to the "All
                // pages" sentinel so the operator doesn't carry a
                // stale, irrelevant selection. Switching to
                // PAGE_VERSION leaves the selection in place (the user
                // may already have picked the right page).
                if (next === "CONTENT_OVERRIDES") setPageId("");
              }}
            >
              <FormControlLabel
                value="PAGE_VERSION"
                control={<Radio data-testid="experiment-create-target-kind-page-version" />}
                label="Whole page"
              />
              <FormControlLabel
                value="CONTENT_OVERRIDES"
                control={<Radio data-testid="experiment-create-target-kind-content-overrides" />}
                label="Block-level overrides"
              />
            </RadioGroup>
            <FormHelperText>
              {isPageVersion
                ? "Variants swap the entire page. Each variant points at a published or experiment PageVersion."
                : "Variants override specific blocks on top of the published page (or across the site for global ContentItems)."}
            </FormHelperText>
          </FormControl>
          <FormControl fullWidth disabled={pagesEmpty} required={pageRequired}>
            <InputLabel id="experiment-create-page-select-label">Page</InputLabel>
            <Select
              labelId="experiment-create-page-select-label"
              label="Page"
              value={pageId}
              onChange={(e) => setPageId(e.target.value)}
              data-testid="experiment-create-page-select"
              endAdornment={
                pagesLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", mr: 3 }}>
                    <CircularProgress size={16} />
                  </Box>
                ) : null
              }
            >
              {/* Issue #247: the "All pages" sentinel is hidden for
                  PAGE_VERSION experiments — they REQUIRE a page. We
                  keep the sentinel for CONTENT_OVERRIDES (site-scoped
                  overrides are valid for global ContentItems). */}
              {!isPageVersion ? (
                <MenuItem value="" data-testid="experiment-create-page-select-option-all">
                  <em>All pages on this site</em>
                </MenuItem>
              ) : null}
              {pageList.map((p) => (
                <MenuItem
                  key={p.id}
                  value={p.id}
                  data-testid={`experiment-create-page-select-option-${p.id}`}
                >
                  {p.path}
                  {p.type ? ` · ${p.type}` : ""}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {pagesEmpty
                ? "This site has no pages yet — create one first to make a page-scoped experiment."
                : pageRequired
                  ? "Required. Pick the page whose versions the variants will swap between."
                  : "Optional. Leave as 'All pages' for a site-scoped experiment."}
            </FormHelperText>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={create.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="experiment-create-submit"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ExperimentsPage(): ReactElement {
  const navigate = useNavigate();
  const sites = useSites({ page: 1, pageSize: 100 });
  const sitesById = useMemo(() => indexSitesById(sites.data?.data), [sites.data?.data]);
  const allSites = sites.data?.data ?? [];

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [siteFilter, setSiteFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<ExperimentStatus | "">("");
  // Issue #253: when unchecked, the list excludes ARCHIVED rows. Default
  // false — operators rarely want to see archived experiments on the
  // primary screen.
  const [showArchived, setShowArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // Default to the first site once it loads — the experiments query is
  // gated on `siteId` so we'd otherwise show an empty page until the
  // operator picked one.
  if (!siteFilter && allSites.length > 0) {
    const firstId = allSites[0]!.id;
    setSiteFilter(firstId);
  }

  const experiments = useExperiments({
    siteId: siteFilter,
    page,
    pageSize,
    status: statusFilter || undefined,
    includeArchived: showArchived,
  });

  // Issue #253: alongside the primary list (default-hides-archived), the
  // page shows "(N archived)" hint when archived rows exist but are
  // hidden. We fetch ONLY the archived count via a second query that
  // sets status=ARCHIVED — the meta.total is the count we want. The
  // query is disabled when the operator's already showing archived
  // rows (the hint would be redundant). Page size 1 keeps the payload
  // small; total is what we actually need.
  const archivedCountQuery = useExperiments({
    siteId: siteFilter,
    page: 1,
    pageSize: 1,
    status: "ARCHIVED",
  });
  const archivedCount =
    !showArchived && archivedCountQuery.data ? (archivedCountQuery.data.meta?.total ?? 0) : 0;

  const handleResetPageOnFilterChange = () => setPage(1);

  const handleRowClick = (id: string) => {
    void navigate({ to: `/experiments/${id}` });
  };

  return (
    <Stack spacing={3} data-testid="experiments-page">
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="h2" data-testid="experiments-page-heading">
            Experiments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A/B/C tests scoped to a site or a single page. Add variants, set weights, and start the
            experiment when ready.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!siteFilter}
          onClick={() => setCreateOpen(true)}
          data-testid="experiments-create-button"
        >
          New experiment
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }} data-testid="experiments-filter-bar">
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel id="experiments-filter-site-label">Site</InputLabel>
            <Select
              labelId="experiments-filter-site-label"
              label="Site"
              value={siteFilter}
              onChange={(e) => {
                setSiteFilter(e.target.value);
                handleResetPageOnFilterChange();
              }}
              data-testid="experiments-filter-site"
            >
              {allSites.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
            <InputLabel id="experiments-filter-status-label">Status</InputLabel>
            <Select<ExperimentStatus | "">
              labelId="experiments-filter-status-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                handleResetPageOnFilterChange();
              }}
              data-testid="experiments-filter-status"
            >
              <MenuItem value="">
                <em>All statuses</em>
              </MenuItem>
              {EXPERIMENT_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Issue #253: show archived toggle. When unchecked (default),
              the list excludes ARCHIVED rows. */}
          <FormControlLabel
            control={
              <Checkbox
                checked={showArchived}
                onChange={(e) => {
                  setShowArchived(e.target.checked);
                  handleResetPageOnFilterChange();
                }}
                data-testid="experiments-list-show-archived-checkbox"
              />
            }
            label="Show archived"
          />
        </Stack>
        {/* Issue #253: discoverability hint — "(N archived)" surfaces
            next to the filter row when archived rows exist but are
            hidden, so the operator knows the toggle exists without
            having to flip it first. Hidden when zero or when archived
            rows are already visible. */}
        {archivedCount > 0 ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
            data-testid="experiments-list-archived-hint"
          >
            ({archivedCount} archived hidden — toggle &quot;Show archived&quot; to reveal)
          </Typography>
        ) : null}
      </Paper>

      {experiments.isError ? (
        <Alert severity="error" data-testid="experiments-list-error">
          {experiments.error.message}
        </Alert>
      ) : null}

      {/* Table view (md+) */}
      <Paper variant="outlined" sx={{ display: { xs: "none", md: "block" } }}>
        <TableContainer>
          <Table aria-label="Experiments" data-testid="experiments-list-table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Variants</TableCell>
                <TableCell>Goal</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {experiments.isPending ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : experiments.data && experiments.data.data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ py: 6 }}
                    data-testid="experiments-list-empty"
                  >
                    <Typography variant="body2" color="text.secondary">
                      {sitesById.size === 0
                        ? "Create a site first to add experiments."
                        : "No experiments yet on this site."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                experiments.data?.data.map((exp) => (
                  <ExperimentRow
                    key={exp.id}
                    experiment={exp}
                    onClick={() => handleRowClick(exp.id)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {experiments.data?.meta ? (
          <TablePagination
            component="div"
            count={experiments.data.meta.total}
            page={page - 1}
            onPageChange={(_e, next) => setPage(next + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(1);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        ) : null}
      </Paper>

      {/* Card list view (xs/sm) */}
      <Box sx={{ display: { xs: "block", md: "none" } }} data-testid="experiments-list-cards">
        {experiments.isPending ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : experiments.data && experiments.data.data.length === 0 ? (
          <Paper variant="outlined" sx={{ py: 6, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No experiments yet on this site.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {experiments.data?.data.map((exp) => (
              <ExperimentCard
                key={exp.id}
                experiment={exp}
                onClick={() => handleRowClick(exp.id)}
              />
            ))}
          </Stack>
        )}
      </Box>

      <CreateExperimentDialog
        open={createOpen}
        siteId={siteFilter}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => handleRowClick(id)}
      />
    </Stack>
  );
}
